import { createDefaultQuickFillProperties } from '../../stores/useQuickFillStore';
import type {
  ConnectorBindingConfiguration,
  MenuConfiguration,
  Option,
  QuickFillProperty,
} from '../../types';
import {
  buildPipeexUiWizardSnapshot,
  normalizePipeexUiWizardSnapshot,
  PIPEEX_UI_PROCESS_FIELD_IDS,
} from './pipeexWizardProfileState';
import type {
  PipeexUiProcessPropertiesSnapshot,
  PipeexUiWizardProfileSource,
  PipeexUiWizardSnapshot,
} from './pipeexWizardProfileState';

export { PIPEEX_UI_PROCESS_FIELD_IDS } from './pipeexWizardProfileState';
export type {
  PipeexUiAdditionalCategoriesFieldId,
  PipeexUiBoltFieldId,
  PipeexUiConnectorFieldId,
  PipeexUiProcessFieldId,
  PipeexUiProcessPropertiesSnapshot,
  PipeexUiWizardAdditionalCategoriesSnapshot,
  PipeexUiWizardBoltSnapshot,
  PipeexUiWizardConnectorSnapshot,
  PipeexUiWizardMappingSnapshot,
  PipeexUiWizardMenuSnapshot,
  PipeexUiWizardProcessPropertiesSnapshot,
  PipeexUiWizardProgressSectionsSnapshot,
  PipeexUiWizardProgressSnapshot,
  PipeexUiWizardProfileSource,
  PipeexUiWizardSetupSnapshot,
  PipeexUiWizardSnapshot,
} from './pipeexWizardProfileState';

export interface QuickFillProfilePropertySnapshot {
  id: string;
  label: string;
  value: string;
  checked: boolean;
  revitParameter?: string;
  isInstance?: boolean;
}

export interface QuickFillProfileSnapshot {
  properties: QuickFillProfilePropertySnapshot[];
  extraProperties: QuickFillProfilePropertySnapshot[];
}

export interface PipeexUiScopeEditorStateSnapshot {
  deletedRowIds: string[];
  customRows: Array<{
    id: string;
    label: string;
    value: string;
  }>;
  pcfAttributeNames: Record<string, string>;
}

export interface PipeexUiScopeEditorsSnapshot {
  processConditions?: PipeexUiScopeEditorStateSnapshot;
  projectProperties?: PipeexUiScopeEditorStateSnapshot;
}

export interface PipeexUiProfileMetadata {
  version: 1 | 2;
  workflowOption: Option;
  workflow: {
    processConditionsChecked: boolean;
    projectPropertiesChecked: boolean;
  };
  quickFill: QuickFillProfileSnapshot;
  processProperties: PipeexUiProcessPropertiesSnapshot;
  scopeEditors: PipeexUiScopeEditorsSnapshot;
  wizard?: PipeexUiWizardSnapshot;
}

export interface QuickFillProfileConfiguration {
  properties: QuickFillProperty[];
  extraProperties: QuickFillProperty[];
}

const KNOWN_WORKFLOW_OPTIONS = new Set<Exclude<Option, null>>([
  'Iso Drawings Generation',
  'Pressure Drop Calculations',
  'Stress Calculations',
]);

const BUILT_IN_PROPERTY_IDS = new Set(
  createDefaultQuickFillProperties().map((property) => property.id)
);

const getTextValue = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const parseJson = <T>(value: string | null | undefined, fallback: T): T => {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isStringRecord = (value: unknown): value is Record<string, string> =>
  typeof value === 'object' &&
  value !== null &&
  Object.values(value).every((entry) => typeof entry === 'string');

const normalizeProcessPropertiesSnapshot = (
  raw: Partial<Record<string, unknown>> | null | undefined
): PipeexUiProcessPropertiesSnapshot => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  return PIPEEX_UI_PROCESS_FIELD_IDS.reduce<PipeexUiProcessPropertiesSnapshot>((snapshot, fieldId) => {
    if (Object.prototype.hasOwnProperty.call(raw, fieldId)) {
      snapshot[fieldId] = getTextValue(raw[fieldId]);
    }
    return snapshot;
  }, {});
};

const normalizeScopeEditorStateSnapshot = (
  raw: unknown
): PipeexUiScopeEditorStateSnapshot | undefined => {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  const parsed = raw as {
    deletedRowIds?: unknown;
    customRows?: unknown;
    pcfAttributeNames?: unknown;
  };

  return {
    deletedRowIds: isStringArray(parsed.deletedRowIds) ? parsed.deletedRowIds : [],
    customRows: Array.isArray(parsed.customRows)
      ? parsed.customRows
          .filter(
            (entry): entry is { id: string; label: string; value: string } =>
              typeof entry === 'object' &&
              entry !== null &&
              typeof (entry as { id?: unknown }).id === 'string' &&
              typeof (entry as { label?: unknown }).label === 'string' &&
              typeof (entry as { value?: unknown }).value === 'string'
          )
          .map((entry) => ({
            id: entry.id,
            label: entry.label,
            value: entry.value,
          }))
      : [],
    pcfAttributeNames: isStringRecord(parsed.pcfAttributeNames) ? parsed.pcfAttributeNames : {},
  };
};

const normalizeScopeEditorsSnapshot = (
  raw: PipeexUiScopeEditorsSnapshot | null | undefined
): PipeexUiScopeEditorsSnapshot => ({
  processConditions: normalizeScopeEditorStateSnapshot(raw?.processConditions),
  projectProperties: normalizeScopeEditorStateSnapshot(raw?.projectProperties),
});

const getPropertyKey = (property: { id?: string; label?: string }): string => {
  const normalizedId = getTextValue(property.id).trim().toLowerCase();
  if (normalizedId) {
    return `id:${normalizedId}`;
  }

  return `label:${getTextValue(property.label).trim().toLowerCase()}`;
};

const toStoreProperty = (
  property: QuickFillProfilePropertySnapshot
): QuickFillProperty => ({
  id: property.id,
  label: property.label,
  value: property.value,
  checked: property.checked,
  revitParameter: property.revitParameter,
  isInstance: property.isInstance,
  options: [],
});

const toSnapshotProperty = (
  property: QuickFillProperty | QuickFillProfilePropertySnapshot
): QuickFillProfilePropertySnapshot => ({
  id: getTextValue(property.id).trim(),
  label: getTextValue(property.label).trim(),
  value: getTextValue(property.value),
  checked: property.checked !== false,
  revitParameter: getTextValue(property.revitParameter).trim() || undefined,
  ...(typeof property.isInstance === 'boolean' ? { isInstance: property.isInstance } : {}),
});

const normalizeSnapshotProperty = (
  raw: unknown
): QuickFillProfilePropertySnapshot | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const normalized = toSnapshotProperty(raw as QuickFillProfilePropertySnapshot);
  if (!normalized.id && !normalized.label) {
    return null;
  }

  return {
    ...normalized,
    id: normalized.id || normalized.label.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    label: normalized.label || normalized.id,
  };
};

const normalizeQuickFillSnapshot = (
  snapshot: QuickFillProfileSnapshot | null | undefined
): QuickFillProfileSnapshot => {
  const normalizedProperties = (Array.isArray(snapshot?.properties) ? snapshot.properties : [])
    .map(normalizeSnapshotProperty)
    .filter(Boolean) as QuickFillProfilePropertySnapshot[];

  const normalizedExtraProperties = (Array.isArray(snapshot?.extraProperties) ? snapshot.extraProperties : [])
    .map(normalizeSnapshotProperty)
    .filter((property): property is QuickFillProfilePropertySnapshot =>
      property !== null && !BUILT_IN_PROPERTY_IDS.has(property.id)
    );

  return {
    properties: normalizedProperties,
    extraProperties: normalizedExtraProperties,
  };
};

export function normalizeWorkflowOption(raw: unknown): Option {
  if (typeof raw !== 'string') {
    return null;
  }

  const normalized = raw.trim();
  return KNOWN_WORKFLOW_OPTIONS.has(normalized as Exclude<Option, null>)
    ? (normalized as Exclude<Option, null>)
    : null;
}

export function buildQuickFillConfigMapping(
  menu: Pick<MenuConfiguration, 'sKeyValue' | 'objectTypeValue' | 'pipingSpecValue'>,
  connector: Pick<
    ConnectorBindingConfiguration,
    | 'endpoint1Value'
    | 'endpoint2Value'
    | 'endpoint3Value'
    | 'endpoint4Value'
    | 'materialValue'
    | 'designStdValue'
    | 'pressureClassValue'
    | 'insulationSpecValue'
    | 'insulationThicknessValue'
    | 'tracingSpecValue'
    | 'itemDescriptionValue'
  >
): Record<string, string> {
  return {
    skey: menu.sKeyValue || '',
    'object-type': menu.objectTypeValue || '',
    'piping-spec': menu.pipingSpecValue || '',
    end1: connector.endpoint1Value?.value || '',
    end2: connector.endpoint2Value?.value || '',
    end3: connector.endpoint3Value?.value || '',
    end4: connector.endpoint4Value?.value || '',
    material: connector.materialValue?.value || '',
    'design-std': connector.designStdValue?.value || '',
    'pressure-class': connector.pressureClassValue?.value || '',
    'insulation-spec': connector.insulationSpecValue?.value || '',
    'insulation-thickness': connector.insulationThicknessValue?.value || '',
    'tracing-spec': connector.tracingSpecValue?.value || '',
    'item-description': connector.itemDescriptionValue?.value || '',
  };
}

export function applyQuickFillConfigToProperties(
  properties: QuickFillProperty[],
  menu: Pick<MenuConfiguration, 'sKeyValue' | 'objectTypeValue' | 'pipingSpecValue'>,
  connector: Pick<
    ConnectorBindingConfiguration,
    | 'endpoint1Value'
    | 'endpoint2Value'
    | 'endpoint3Value'
    | 'endpoint4Value'
    | 'materialValue'
    | 'designStdValue'
    | 'pressureClassValue'
    | 'insulationSpecValue'
    | 'insulationThicknessValue'
    | 'tracingSpecValue'
    | 'itemDescriptionValue'
  >
): QuickFillProperty[] {
  const mapping = buildQuickFillConfigMapping(menu, connector);

  return properties.map((property) => {
    const mappedParameter = mapping[property.id];
    const nextRevitParameter =
      typeof mappedParameter === 'string' && mappedParameter.trim().length > 0
        ? mappedParameter
        : property.revitParameter;

    return {
      ...property,
      value: getTextValue(property.value),
      checked: property.checked !== false,
      revitParameter: nextRevitParameter,
      options: [],
    };
  });
}

export function buildQuickFillProfileSnapshot(
  properties: QuickFillProperty[] | undefined,
  extraProperties: QuickFillProperty[] | undefined,
  menu: Pick<MenuConfiguration, 'sKeyValue' | 'objectTypeValue' | 'pipingSpecValue'>,
  connector: Pick<
    ConnectorBindingConfiguration,
    | 'endpoint1Value'
    | 'endpoint2Value'
    | 'endpoint3Value'
    | 'endpoint4Value'
    | 'materialValue'
    | 'designStdValue'
    | 'pressureClassValue'
    | 'insulationSpecValue'
    | 'insulationThicknessValue'
    | 'tracingSpecValue'
    | 'itemDescriptionValue'
  >
): QuickFillProfileSnapshot {
  const baseProperties = Array.isArray(properties) && properties.length > 0
    ? properties
    : createDefaultQuickFillProperties();

  return {
    properties: applyQuickFillConfigToProperties(baseProperties, menu, connector)
      .filter((property) => BUILT_IN_PROPERTY_IDS.has(property.id))
      .map(toSnapshotProperty),
    extraProperties: (Array.isArray(extraProperties) ? extraProperties : [])
      .filter((property) => property && !BUILT_IN_PROPERTY_IDS.has(property.id))
      .map((property) => toSnapshotProperty({ ...property, options: [] })),
  };
}

export function resolveQuickFillProfileSnapshot(
  snapshot: QuickFillProfileSnapshot | null | undefined,
  menu: Pick<MenuConfiguration, 'sKeyValue' | 'objectTypeValue' | 'pipingSpecValue'>,
  connector: Pick<
    ConnectorBindingConfiguration,
    | 'endpoint1Value'
    | 'endpoint2Value'
    | 'endpoint3Value'
    | 'endpoint4Value'
    | 'materialValue'
    | 'designStdValue'
    | 'pressureClassValue'
    | 'insulationSpecValue'
    | 'insulationThicknessValue'
    | 'tracingSpecValue'
    | 'itemDescriptionValue'
  >
): QuickFillProfileSnapshot {
  const normalizedSnapshot = normalizeQuickFillSnapshot(snapshot);
  const savedPropertyMap = new Map(
    normalizedSnapshot.properties.map((property) => [getPropertyKey(property), property])
  );

  const resolvedProperties = applyQuickFillConfigToProperties(
    createDefaultQuickFillProperties(),
    menu,
    connector
  ).map((defaultProperty) => {
    const savedProperty = savedPropertyMap.get(getPropertyKey(defaultProperty));
    if (!savedProperty) {
      return toSnapshotProperty(defaultProperty);
    }

    return toSnapshotProperty({
      ...defaultProperty,
      value: savedProperty.value,
      checked: savedProperty.checked,
      revitParameter: savedProperty.revitParameter || defaultProperty.revitParameter,
      options: [],
    });
  });

  return {
    properties: resolvedProperties,
    extraProperties: normalizedSnapshot.extraProperties.map((property) =>
      toSnapshotProperty({
        ...toStoreProperty(property),
        options: [],
      })
    ),
  };
}

export function restoreQuickFillProfileConfiguration(
  snapshot: QuickFillProfileSnapshot | null | undefined,
  menu: Pick<MenuConfiguration, 'sKeyValue' | 'objectTypeValue' | 'pipingSpecValue'>,
  connector: Pick<
    ConnectorBindingConfiguration,
    | 'endpoint1Value'
    | 'endpoint2Value'
    | 'endpoint3Value'
    | 'endpoint4Value'
    | 'materialValue'
    | 'designStdValue'
    | 'pressureClassValue'
    | 'insulationSpecValue'
    | 'insulationThicknessValue'
    | 'tracingSpecValue'
    | 'itemDescriptionValue'
  >
): QuickFillProfileConfiguration {
  const resolvedSnapshot = resolveQuickFillProfileSnapshot(snapshot, menu, connector);

  return {
    properties: resolvedSnapshot.properties.map(toStoreProperty),
    extraProperties: resolvedSnapshot.extraProperties.map(toStoreProperty),
  };
}

export function createPipeexUiProfileMetadata(
  workflowOption: Option,
  workflow: {
    processConditionsChecked: boolean;
    projectPropertiesChecked: boolean;
  },
  processProperties: PipeexUiProcessPropertiesSnapshot,
  properties: QuickFillProperty[] | undefined,
  extraProperties: QuickFillProperty[] | undefined,
  menu: Pick<MenuConfiguration, 'sKeyValue' | 'objectTypeValue' | 'pipingSpecValue'>,
  connector: Pick<
    ConnectorBindingConfiguration,
    | 'endpoint1Value'
    | 'endpoint2Value'
    | 'endpoint3Value'
    | 'endpoint4Value'
    | 'materialValue'
    | 'designStdValue'
    | 'pressureClassValue'
    | 'insulationSpecValue'
    | 'insulationThicknessValue'
    | 'tracingSpecValue'
    | 'itemDescriptionValue'
  >,
  scopeEditors?: PipeexUiScopeEditorsSnapshot | null,
  wizardSource?: PipeexUiWizardProfileSource | null
): PipeexUiProfileMetadata {
  const wizard = buildPipeexUiWizardSnapshot(wizardSource);

  return {
    version: 2,
    workflowOption: normalizeWorkflowOption(workflowOption),
    workflow: {
      processConditionsChecked: workflow.processConditionsChecked,
      projectPropertiesChecked: workflow.projectPropertiesChecked,
    },
    processProperties: normalizeProcessPropertiesSnapshot(processProperties),
    quickFill: buildQuickFillProfileSnapshot(
      properties,
      extraProperties,
      menu,
      connector
    ),
    scopeEditors: normalizeScopeEditorsSnapshot(scopeEditors),
    ...(wizard ? { wizard } : {}),
  };
}

export function getPipeexUiProfileMetadata(
  advancedSettings: string | null | undefined
): PipeexUiProfileMetadata | null {
  const advancedRaw = parseJson<any>(advancedSettings, {});
  const metadataRaw = typeof advancedRaw?.pipeexUi === 'string'
    ? parseJson<any>(advancedRaw.pipeexUi, null)
    : advancedRaw?.pipeexUi;

  if (!metadataRaw || typeof metadataRaw !== 'object') {
    return null;
  }

  const quickFill = normalizeQuickFillSnapshot(
    (metadataRaw as { quickFill?: QuickFillProfileSnapshot }).quickFill
  );
  const workflowOption = normalizeWorkflowOption(
    (metadataRaw as { workflowOption?: unknown }).workflowOption
  );
  const workflowRaw = (metadataRaw as {
    workflow?: {
      processConditionsChecked?: unknown;
      projectPropertiesChecked?: unknown;
    };
  }).workflow;
  const workflow = {
    processConditionsChecked: Boolean(workflowRaw?.processConditionsChecked),
    projectPropertiesChecked: Boolean(workflowRaw?.projectPropertiesChecked),
  };
  const processProperties = normalizeProcessPropertiesSnapshot(
    (metadataRaw as { processProperties?: Partial<Record<string, unknown>> }).processProperties
  );
  const scopeEditors = normalizeScopeEditorsSnapshot(
    (metadataRaw as { scopeEditors?: PipeexUiScopeEditorsSnapshot }).scopeEditors
  );
  const wizard = normalizePipeexUiWizardSnapshot(
    (metadataRaw as { wizard?: unknown }).wizard
  );
  const hasProcessPropertiesSnapshot = PIPEEX_UI_PROCESS_FIELD_IDS.some(
    (fieldId) => getTextValue(processProperties[fieldId]).trim().length > 0
  );
  const hasScopeEditorSnapshot = Boolean(
    scopeEditors.processConditions?.deletedRowIds.length ||
      scopeEditors.processConditions?.customRows.length ||
      Object.keys(scopeEditors.processConditions?.pcfAttributeNames || {}).length ||
      scopeEditors.projectProperties?.deletedRowIds.length ||
      scopeEditors.projectProperties?.customRows.length ||
      Object.keys(scopeEditors.projectProperties?.pcfAttributeNames || {}).length
  );

  if (
    !wizard &&
    workflowOption === null &&
    !workflow.processConditionsChecked &&
    !workflow.projectPropertiesChecked &&
    quickFill.properties.length === 0 &&
    quickFill.extraProperties.length === 0 &&
    !hasProcessPropertiesSnapshot &&
    !hasScopeEditorSnapshot
  ) {
    return null;
  }

  return {
    version: wizard ? 2 : 1,
    workflowOption,
    workflow,
    processProperties,
    quickFill,
    scopeEditors,
    ...(wizard ? { wizard } : {}),
  };
}

export function countQuickFillSnapshotDifferences(
  currentSnapshot: QuickFillProfileSnapshot,
  profileSnapshot: QuickFillProfileSnapshot
): number {
  const currentMap = new Map<string, string>();
  const profileMap = new Map<string, string>();

  [...currentSnapshot.properties, ...currentSnapshot.extraProperties].forEach((property) => {
    currentMap.set(
      getPropertyKey(property),
      `${property.checked ? '1' : '0'}::${property.revitParameter || ''}::${typeof property.isInstance === 'boolean' ? property.isInstance : ''}`
    );
  });

  [...profileSnapshot.properties, ...profileSnapshot.extraProperties].forEach((property) => {
    profileMap.set(
      getPropertyKey(property),
      `${property.checked ? '1' : '0'}::${property.revitParameter || ''}::${typeof property.isInstance === 'boolean' ? property.isInstance : ''}`
    );
  });

  const keys = new Set([...currentMap.keys(), ...profileMap.keys()]);
  let differenceCount = 0;

  keys.forEach((key) => {
    if ((currentMap.get(key) || '') !== (profileMap.get(key) || '')) {
      differenceCount += 1;
    }
  });

  return differenceCount;
}

export function countQuickFillBindingDifferences(
  snapshot: QuickFillProfileSnapshot,
  menu: Pick<MenuConfiguration, 'sKeyValue' | 'objectTypeValue' | 'pipingSpecValue'>,
  connector: Pick<
    ConnectorBindingConfiguration,
    | 'endpoint1Value'
    | 'endpoint2Value'
    | 'endpoint3Value'
    | 'endpoint4Value'
    | 'materialValue'
    | 'designStdValue'
    | 'pressureClassValue'
    | 'insulationSpecValue'
    | 'insulationThicknessValue'
    | 'tracingSpecValue'
    | 'itemDescriptionValue'
  >
): number {
  const expectedBindings = buildQuickFillConfigMapping(menu, connector);

  return snapshot.properties.reduce((differenceCount, property) => {
    const expectedBinding = expectedBindings[property.id];
    const actualBinding = getTextValue(property.revitParameter).trim();

    if (
      expectedBinding &&
      expectedBinding.trim().length > 0 &&
      actualBinding !== expectedBinding
    ) {
      return differenceCount + 1;
    }

    return differenceCount;
  }, 0);
}
